/**
 * Public and workspace URL helpers for Gistoscope.
 *
 * This file is the source of truth for user-facing URL shapes. Do not manually
 * concatenate public subject/gist/comment/anchor URLs or workspace gist/subject
 * URLs in React components or server route code. Use these helpers so future
 * canonical URL changes stay localized and testable.
 *
 * Canonical public URL contract:
 *   Subject: /s/{subjectSlug}--{subjectPublicId}
 *   Gist:    /s/{subjectSlug}--{subjectPublicId}/{gistSlug}--{gistPublicId}
 *
 * Workspace URL contract:
 *   Subject workspace: /workspace/s/{subjectPublicId}
 *   Gist workspace:    /workspace/g/{gistPublicId}?mode={read|edit|advanced}
 *
 * Direct public gist fallback:
 *   /g/{gistPublicId}
 *
 * Important invariant:
 *   Numeric database IDs are internal implementation details. New public,
 *   workspace, import/export, copy-link, and internal-link URL helpers must use
 *   stable publicId values and must not fall back to database IDs.
 */

export type PublicSubjectLike = {
  id?: string | number | null;
  publicId?: string | null;
  title?: string | null;
  slug?: string | null;
};

export type PublicGistLike = {
  id?: string | number | null;
  publicId?: string | null;
  title?: string | null;
  slug?: string | null;
};

export type PublicCommentLike = {
  id?: string | number | null;
  publicId?: string | null;
};

export type PublicAnchorLike = {
  id?: string | number | null;
  publicId?: string | null;
};

export type PublicUserLike = {
  id?: string | number | null;
  publicId?: string | null;
  handle?: string | null;
  name?: string | null;
  displayName?: string | null;
};

export type PublicIdLike = string | { publicId?: string | null };

export type WorkspaceGistMode = "read" | "edit" | "advanced";

export type ParsedSubjectSlugWithId = {
  subjectSlug: string;
  subjectPublicId: string;
};

export type ParsedGistSlugWithId = {
  gistSlug: string;
  gistPublicId: string;
};

const FALLBACK_SLUG = "item";
const DEFAULT_WORKSPACE_GIST_MODE: WorkspaceGistMode = "read";
const WORKSPACE_GIST_MODES: readonly WorkspaceGistMode[] = ["read", "edit", "advanced"];

const RESERVED_PUBLIC_SEGMENTS = new Set([
  "api",
  "admin",
  "auth",
  "dashboard",
  "design-lab",
  "g",
  "login",
  "logout",
  "navigator",
  "private",
  "profile",
  "settings",
  "signup",
  "subject",
  "subject-single",
  "workspace",
]);

export function slugifyTitle(input: string | null | undefined): string {
  const normalized = String(input ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['’]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");

  return normalized || FALLBACK_SLUG;
}

export function isReservedPublicSegment(value: string | null | undefined): boolean {
  return RESERVED_PUBLIC_SEGMENTS.has(slugifyTitle(value));
}

/**
 * Legacy-compatible public URL id encoder.
 *
 * Existing public URL helpers still tolerate old data without publicId by
 * falling back to id. Do not use this for new workspace, copy-link, import, or
 * internal-link helpers. Those must require stable publicId values.
 */
export function publicIdForUrl(
  entity: { publicId?: string | null; id?: string | number | null },
  fallbackPrefix: string,
): string {
  const raw = entity.publicId ?? (entity.id !== undefined && entity.id !== null ? `${fallbackPrefix}${entity.id}` : null);
  return encodeURIComponent(String(raw ?? `${fallbackPrefix}unknown`));
}

function stablePublicIdValue(value: PublicIdLike | null | undefined, entityName: string): string {
  const raw = typeof value === "string" ? value : value?.publicId;
  const normalized = String(raw ?? "").trim();

  if (!normalized) {
    throw new Error(`${entityName} publicId is required for stable public/workspace URLs.`);
  }

  return normalized;
}

export function stablePublicIdForUrl(value: PublicIdLike | null | undefined, entityName: string): string {
  return encodeURIComponent(stablePublicIdValue(value, entityName));
}

export function subjectSlugForUrl(subject: PublicSubjectLike): string {
  return encodeURIComponent(subject.slug || slugifyTitle(subject.title) || publicIdForUrl(subject, "s_"));
}

export function subjectCodeForUrl(subject: PublicSubjectLike): string {
  return publicIdForUrl(subject, "s_");
}

export function subjectSegmentForUrl(subject: PublicSubjectLike): string {
  return `${subjectSlugForUrl(subject)}--${subjectCodeForUrl(subject)}`;
}

export function gistSlugForUrl(gist: PublicGistLike): string {
  return encodeURIComponent(gist.slug || slugifyTitle(gist.title) || publicIdForUrl(gist, "g_"));
}

export function gistCodeForUrl(gist: PublicGistLike): string {
  return publicIdForUrl(gist, "g_");
}

export function gistSegmentForUrl(gist: PublicGistLike): string {
  return `${gistSlugForUrl(gist)}--${gistCodeForUrl(gist)}`;
}

export function buildSubjectUrl(subject: PublicSubjectLike): string {
  return `/s/${subjectSegmentForUrl(subject)}`;
}

export function buildGistUrl(subject: PublicSubjectLike, gist: PublicGistLike): string {
  return `${buildSubjectUrl(subject)}/${gistSegmentForUrl(gist)}`;
}

export function buildCommentUrl(subject: PublicSubjectLike, gist: PublicGistLike, comment: PublicCommentLike): string {
  return `${buildGistUrl(subject, gist)}#comment-${publicIdForUrl(comment, "c_")}`;
}

export function buildAnchorUrl(subject: PublicSubjectLike, gist: PublicGistLike, anchor: PublicAnchorLike): string {
  return `${buildGistUrl(subject, gist)}#anchor-${publicIdForUrl(anchor, "a_")}`;
}

export function buildUserUrl(user: PublicUserLike): string {
  const handleOrFallback = user.handle || slugifyTitle(user.displayName || user.name) || publicIdForUrl(user, "u_");
  return `/@${encodeURIComponent(handleOrFallback)}`;
}

export function buildDirectGistUrl(gist: PublicIdLike): string {
  return `/g/${stablePublicIdForUrl(gist, "Gist")}`;
}

export function normalizeWorkspaceGistMode(mode: string | null | undefined): WorkspaceGistMode {
  return (WORKSPACE_GIST_MODES as readonly string[]).includes(String(mode ?? ""))
    ? (mode as WorkspaceGistMode)
    : DEFAULT_WORKSPACE_GIST_MODE;
}

export function buildWorkspaceSubjectUrl(subject: PublicIdLike): string {
  return `/workspace/s/${stablePublicIdForUrl(subject, "Subject")}`;
}

export function buildWorkspaceGistUrl(gist: PublicIdLike, mode: string | null | undefined = DEFAULT_WORKSPACE_GIST_MODE): string {
  const normalizedMode = normalizeWorkspaceGistMode(mode);
  return `/workspace/g/${stablePublicIdForUrl(gist, "Gist")}?mode=${encodeURIComponent(normalizedMode)}`;
}

function parseSlugWithId(segment: string): { slug: string; publicId: string } | null {
  const decoded = decodeURIComponent(segment || "");
  const separatorIndex = decoded.lastIndexOf("--");

  if (separatorIndex <= 0 || separatorIndex >= decoded.length - 2) {
    return null;
  }

  return {
    slug: decoded.slice(0, separatorIndex),
    publicId: decoded.slice(separatorIndex + 2),
  };
}

export function parseSubjectSlugWithId(segment: string): ParsedSubjectSlugWithId | null {
  const parsed = parseSlugWithId(segment);
  if (!parsed) return null;

  return {
    subjectSlug: parsed.slug,
    subjectPublicId: parsed.publicId,
  };
}

export function parseGistSlugWithId(segment: string): ParsedGistSlugWithId | null {
  const parsed = parseSlugWithId(segment);
  if (!parsed) return null;

  return {
    gistSlug: parsed.slug,
    gistPublicId: parsed.publicId,
  };
}

export function normalizeHashTarget(hash: string | null | undefined): string | null {
  const clean = String(hash ?? "").replace(/^#/, "").trim();
  return clean || null;
}
